// frontend/src/components/ChatInterface.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";


export default function ChatInterface({
  title = "Consultation",
  placeholder = "Type your message...",
  messages = [],
  onSend, // (text) => void | Promise<void>; if null, input disabled
  isLoading = false,
  error = null,
  onClear = null,

  // NEW (optional): allows parent to control focus
  inputRef = null,
  autoFocus = false,
}) {
  const [inputMessage, setInputMessage] = useState("");
  const messagesEndRef = useRef(null);

  // Internal ref used if parent doesn't pass one
  const internalInputRef = useRef(null);
  const resolvedInputRef = inputRef || internalInputRef;

  const inputDisabled = isLoading || typeof onSend !== "function";

  const focusInput = () => {
    const el = resolvedInputRef?.current;
    if (!el || inputDisabled) return;
    el.focus();
    // Put caret at end (useful for textarea)
    try {
      const len = el.value?.length ?? 0;
      if (typeof el.setSelectionRange === "function") {
        el.setSelectionRange(len, len);
      }
    } catch {
      // ignore
    }
  };

  // Scroll to bottom on new messages/loading changes (existing behavior)
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isLoading]);

  // Autofocus on mount if requested
  useEffect(() => {
    if (autoFocus) {
      // next tick ensures element exists
      setTimeout(() => focusInput(), 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep focus after assistant replies (or any message append), once loading ends
  useEffect(() => {
    if (!isLoading && typeof onSend === "function") {
      // next tick to allow DOM update
      setTimeout(() => focusInput(), 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages.length, isLoading]);

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "";
    try {
      return new Date(timestamp).toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "";
    }
  };

  const handleSend = async () => {
    const text = (inputMessage || "").trim();
    if (!text || inputDisabled) return;

    // Clear input immediately so user sees it sent
    setInputMessage("");

    // Call parent handler; await if it returns a Promise
    try {
      const maybePromise = onSend(text);
      if (maybePromise && typeof maybePromise.then === "function") {
        await maybePromise;
      }
    } finally {
      // Refocus regardless of success/failure
      setTimeout(() => focusInput(), 0);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const sendDisabled = useMemo(() => {
    return inputDisabled || !inputMessage.trim();
  }, [inputDisabled, inputMessage]);

  return (
    <div className="chat-interface">
      <div className="chat-header">
        <h2>{title}</h2>
        {onClear && (
          <button className="clear-btn" type="button" onClick={onClear}>
            Clear
          </button>
        )}
      </div>

      {error && (
        <div className="error-banner">
          <span>{error}</span>
        </div>
      )}

      <div className="messages-container">
        {messages.map((m, idx) => (
          <div
            key={idx}
            className={`message ${m.role || "assistant"} ${m.isError ? "error" : ""}`}
          >
            <div className="message-content">
              <div className="message-text">
                <ReactMarkdown>{m.content}</ReactMarkdown>
              </div>
              <div className="message-timestamp">{formatTimestamp(m.timestamp)}</div>
            </div>
          </div>
        ))}

        {isLoading && (
          <div className="message assistant loading">
            <div className="message-content">
              <div className="typing-indicator">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      <div className="chat-input-container">
        <div className="chat-input-wrapper">
          <textarea
            ref={resolvedInputRef}
            value={inputMessage}
            onChange={(e) => {
              setInputMessage(e.target.value);
              e.target.style.height = 'auto';
              e.target.style.height = Math.min(e.target.scrollHeight, 150) + 'px';
            }}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={inputDisabled}
            rows={1}
            className="chat-input"
          />
          <button
            onClick={handleSend}
            disabled={sendDisabled}
            className="send-button"
            type="button"
            // Prevent the button from taking focus away from the textarea
            onMouseDown={(e) => e.preventDefault()}
          >
            Send
          </button>
        </div>
      </div>
    </div>
  );
}
